import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithProviderEndpointFirst } from "@/redux/baseQuery";

export interface VeivoValidationStart {
  validationId: string;
  sessionToken: string;
  veivoBaseUrl: string;
  status: string;
}

export interface VeivoValidationStatus {
  validationId: string | null;
  status: string | null;
  projectedAt: string | null;
}

/** DEV-1731: a vehicle's documents as Veivo verified them, per country. */
export interface VeivoVehicleDocuments {
  country: string | null;
  requiredDocuments: Array<{
    docType: string;
    /** Rideminder's type for the same document; joins to vehicle.documents. */
    documentType: "REGISTRATION" | "INSURANCE" | "ACCREDITATION" | null;
    name: string;
    description: string;
    optional: boolean;
    requiresExpiry: boolean;
  }>;
  documents: Array<{
    docType: string;
    documentType: "REGISTRATION" | "INSURANCE" | "ACCREDITATION" | null;
    validationId: string;
    validationDocumentId: string;
    expiryDate: string | null;
    artifactId: string | null;
    /** Short-lived viewer URL; null when one could not be minted. */
    imageUrl: string | null;
    /** Stripe-style test data from a skipped step. */
    isTest: boolean;
    capturedAt: string | null;
  }>;
  pendingValidation: { validationId: string; status: string } | null;
}

export interface VeivoArtifactToken {
  token: string;
  urlTemplate: string;
}

/** Offline VIN decode Veivo returns on create — no paid call, so an invalid VIN
 *  is rejected before anything costs anything. */
export interface VinDecoded {
  valid: boolean;
  vin: string;
  modelYear: number | null;
  wmi?: { manufacturer: string | null; country: string | null } | null;
}

export interface VeivoValidationFromVin extends VeivoValidationStart {
  vinDecoded: VinDecoded | null;
}

/** The car as Find's plate lookup described it (DEV-1897). Display only: the
 *  documents decide the vehicle's identity. */
export interface VeivoVehicleSummary {
  make: string | null;
  model: string | null;
  year: number | null;
  colour: string | null;
  vin?: string | null;
  /** The VIN's last characters, when that is all the registry gives (NSW). */
  vinEnding?: string | null;
  body: string | null;
  fuelType: string | null;
  /** The registry's seat count, where it discloses one (DEV-1897). */
  seats?: number | null;
}

/** A document the country requires, and what it must show (DEV-1892): each
 *  entry a field, or a list of fields any one of which will do. */
export interface VeivoRequiredDocument {
  docType: string;
  name: string;
  description: string;
  optional: boolean;
  requiresExpiry: boolean;
  requiredFields?: Array<string | string[]>;
}

/** GET …/plate-lookup — Find (DEV-1897). */
export interface VeivoFindResult {
  registrationNumber: string;
  country: string;
  state: { uuid: string; abbreviation: string; name: string };
  /** The registry's car. Find refuses (422 VEHICLE_NOT_IN_REGISTRY, 503
   *  REGISTRY_UNAVAILABLE) rather than answer without one; the type stays
   *  nullable only for older servers. */
  vehicle: VeivoVehicleSummary | null;
  requiredDocuments: VeivoRequiredDocument[];
}

/** "Save and finish later" (DEV-1899): the step and what was entered, as the
 *  add-vehicle page holds it. */
export interface VeivoDraft {
  step: number | null;
  data: Record<string, unknown> | null;
  savedAt: string | null;
}

/** GET …/prefill — what the Details step starts from (DEV-1900). */
export interface VeivoPrefill {
  registrationNumber: string | null;
  stateUuid: string | null;
  vin: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  colour: string | null;
  body: string | null;
  fuelType: string | null;
  seats: number | null;
  /** Passengers, the driver not counted. */
  suggestedCapacityPax: number | null;
  photos: Array<{ orientation: string; url: string }>;
}

/** POST …/validations/:id/vehicle — "Add vehicle · $X" at Review (DEV-1898). */
export interface ConfirmVehicleParams {
  validationId: string;
  details: {
    identifier?: string;
    has_markings: boolean;
    capacity_pax: number;
    capacity_bags: number;
    maintenance_fee?: number;
    vehicle_types: string[];
  };
  access: { level: "ALL" | "RESTRICTED"; drivers: string[] };
}

export interface StartFromVinParams {
  /** Deprecated for new starts (DEV-1786): Veivo now reads the VIN off the
   *  registration document. Still accepted by the server. */
  vin?: string;
  /** The plate the operator entered. With no VIN, state_uuid is required too:
   *  the pair is how the server spots a car already in the fleet. */
  registration_number?: string;
  /** The operator's state. Needed to create the vehicle once KYV passes, and
   *  sent to Veivo as a claim to check against the registration document. */
  state_uuid?: string;
  country?: string;
  /** "confirm": the vehicle is created at Review, where the fee is shown
   *  (DEV-1898). Omitted = created when the verification passes. */
  create_on?: "verification" | "confirm";
  /** The car Find showed, for the phone to show too. */
  vehicle_summary?: VeivoVehicleSummary;
}

/** One actionable reason a validation needs fixing. `code` is a machine-readable
 *  check slug, so retake copy can be specific rather than generic. */
export interface VeivoReason {
  code: string;
  outcome: "fail" | "review";
  docType?: string;
  detail: string;
}

export interface VeivoValidationListItem {
  validationId: string;
  /** Null until the validation goes terminal and the vehicle is created. */
  vehicleUuid: string | null;
  /** Null until the registration document gives it (DEV-1786). */
  vin: string | null;
  /** The plate the verification was started from; null for a VIN start. */
  registrationNumber: string | null;
  country: string;
  status: string;
  manufacturer: string | null;
  modelYear: number | null;
  reasonSummary: VeivoReason[] | null;
  updatedAt: string;
  stateUuid?: string | null;
  /** "confirm": a draft of the add-vehicle flow (DEV-1899), with `draft`. */
  createOn?: "verification" | "confirm";
  vehicleSummary?: VeivoVehicleSummary | null;
  draft?: VeivoDraft | null;
}

export type VeivoValidationGroup = "in_progress" | "needs_fixing";

/**
 * Veivo vehicle-verification endpoints (web-app-v2 veivoModule, DEV-1405).
 * The FE never talks to the Veivo service with tenant credentials — v2 mints
 * short-lived session/artifact tokens; completion is decided by polling the
 * server-tracked link status, never the SDK's client-side callback.
 */
export const veivoApi = createApi({
  baseQuery: baseQueryWithProviderEndpointFirst("vehicle"),
  reducerPath: "veivoApi",
  tagTypes: ["VeivoValidation"],
  endpoints: (builder) => ({
    startVeivoValidation: builder.mutation<VeivoValidationStart, string>({
      query: (vehicleId) => ({
        url: `/${vehicleId}/validation`,
        method: "POST",
      }),
      invalidatesTags: (_, __, vehicleId) => [{ type: "VeivoValidation", id: vehicleId }],
    }),
    getVeivoValidation: builder.query<VeivoValidationStatus, string>({
      query: (vehicleId) => `/${vehicleId}/validation`,
      providesTags: (_, __, vehicleId) => [{ type: "VeivoValidation", id: vehicleId }],
    }),
    /** No vehicle exists yet, so this is scoped to the operator. Started from
     *  plate + state since DEV-1786 (a VIN is still accepted). */
    startVeivoValidationFromVin: builder.mutation<
      VeivoValidationFromVin,
      StartFromVinParams
    >({
      query: (body) => ({
        url: `/validation`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["VeivoValidation"],
    }),
    /** Poll by VALIDATION id: the VIN-first flow has no vehicle to key on until
     *  KYV passes, and this is how the FE learns which vehicle was created. */
    getVeivoValidationStatus: builder.query<
      {
        validationId: string;
        status: string | null;
        vehicleUuid: string | null;
        projectionError: string | null;
        /** Set when RideMinder refused the vehicle because the car already
         *  exists — at the end (DEV-1754), or as soon as the registration
         *  document gave the VIN (DEV-1786). Final either way. */
        refusalCode?: "VEHICLE_ALREADY_REGISTERED" | "VEHICLE_ALREADY_IN_FLEET" | null;
        /** Null until the registration document gives it (DEV-1786). */
        vin?: string | null;
        vinDecoded?: VinDecoded | null;
        /** DEV-1902: what the add-vehicle page resumes a draft from. */
        registrationNumber?: string | null;
        stateUuid?: string | null;
        country?: string;
        createOn?: "verification" | "confirm";
        vehicleSummary?: VeivoVehicleSummary | null;
        draft?: VeivoDraft | null;
      },
      string
    >({
      query: (validationId) => `/validations/${validationId}`,
      providesTags: (_, __, validationId) => [{ type: "VeivoValidation", id: validationId }],
    }),
    listVeivoValidations: builder.query<
      { items: VeivoValidationListItem[] },
      VeivoValidationGroup
    >({
      query: (group) => `/validations?group=${group}`,
      providesTags: ["VeivoValidation"],
    }),
    /**
     * Fresh session token for a validation the operator already owns, so a
     * "Continue"/"Retake" link can resume it.
     *
     * Resuming without one gets rejected by Veivo, and the flow then falls back
     * to its own start step — which opens a SECOND verification for a car that
     * already has one.
     */
    /**
     * Abandon an in-progress verification. Deletes it on Veivo and drops the
     * local link, so the entry leaves the list and the car is free to start
     * again. Refused once a vehicle exists.
     */
    deleteVeivoValidation: builder.mutation<void, string>({
      query: (validationId) => ({
        url: `/validations/${validationId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["VeivoValidation"],
    }),
    mintVeivoResumeToken: builder.mutation<
      { validationId: string; sessionToken: string; veivoBaseUrl: string },
      string
    >({
      query: (validationId) => ({
        url: `/validations/${validationId}/session-token`,
        method: "POST",
      }),
    }),
    /**
     * Find (DEV-1897): the plan's room, whether the rego is already a vehicle,
     * what the registry says the car is, and the documents its country needs —
     * before anything is started or paid for.
     */
    findVehicle: builder.query<VeivoFindResult, { registrationNumber: string; stateUuid: string }>({
      query: ({ registrationNumber, stateUuid }) =>
        `/plate-lookup?registration_number=${encodeURIComponent(registrationNumber)}&state_uuid=${encodeURIComponent(stateUuid)}`,
    }),
    /** "Add vehicle · $X" (DEV-1898): web-app creates the vehicle and charges
     *  its fee. Safe to repeat. */
    confirmVehicle: builder.mutation<{ vehicleUuid: string }, ConfirmVehicleParams>({
      query: ({ validationId, ...body }) => ({
        url: `/validations/${validationId}/vehicle`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["VeivoValidation"],
    }),
    /** "Save and finish later" (DEV-1899). */
    saveVeivoDraft: builder.mutation<VeivoDraft, { validationId: string; step: number; data?: Record<string, unknown> }>({
      query: ({ validationId, ...body }) => ({
        url: `/validations/${validationId}/draft`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["VeivoValidation"],
    }),
    getVeivoPrefill: builder.query<VeivoPrefill, string>({
      query: (validationId) => `/validations/${validationId}/prefill`,
    }),
    /** DEV-1732: documents-only renewal of a verified vehicle's documents. */
    startVeivoRenewal: builder.mutation<VeivoValidationStart, { vehicleId: string; docTypes: string[] }>({
      query: ({ vehicleId, docTypes }) => ({
        url: `/${vehicleId}/validation/renewal`,
        method: "POST",
        body: { docTypes },
      }),
      invalidatesTags: ["VeivoValidation"],
    }),
    getVeivoVehicleDocuments: builder.query<VeivoVehicleDocuments, string>({
      query: (vehicleId) => `/${vehicleId}/validation/documents`,
      providesTags: ["VeivoValidation"],
    }),
    mintVeivoArtifactToken: builder.mutation<
      VeivoArtifactToken,
      { vehicleId: string; artifactId?: string }
    >({
      query: ({ vehicleId, artifactId }) => ({
        url: `/${vehicleId}/validation/artifact-token`,
        method: "POST",
        body: artifactId ? { artifactId } : {},
      }),
    }),
  }),
});

export const {
  useStartVeivoValidationMutation,
  useStartVeivoValidationFromVinMutation,
  useGetVeivoValidationQuery,
  useGetVeivoValidationStatusQuery,
  useListVeivoValidationsQuery,
  useMintVeivoArtifactTokenMutation,
  useGetVeivoVehicleDocumentsQuery,
  useStartVeivoRenewalMutation,
  useMintVeivoResumeTokenMutation,
  useDeleteVeivoValidationMutation,
  useLazyFindVehicleQuery,
  useConfirmVehicleMutation,
  useSaveVeivoDraftMutation,
  useGetVeivoPrefillQuery,
} = veivoApi;
